import React from "react";
import {
  FiAperture,
  FiLayers,
  FiRefreshCcw,
  FiCpu,
  FiTrello,
  FiGitBranch,
} from "react-icons/fi";
import { MdOutlineSelfImprovement, MdAutoMode } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const AutonomousAIAgent = () => {
  const features = [
    {
      title: "Multi-Step Reasoning",
      description:
        "Agents plan, reason, and adapt across long-horizon objectives using advanced chain-of-thought orchestration.",
      icon: <FiAperture />,
    },
    {
      title: "Agentic Workflows",
      description:
        "Composable workflows autonomously invoke tools, APIs, and human checkpoints to complete business processes end-to-end.",
      icon: <FiLayers />,
    },
    {
      title: "Self-Learning Behavior",
      description:
        "Feedback loops, reinforcement learning, and observation logs help agents continuously improve performance.",
      icon: <MdOutlineSelfImprovement />,
    },
    {
      title: "Long-Context Decisioning",
      description:
        "Memory stores and vector databases let agents reference extensive histories, documents, and states for accurate decisions.",
      icon: <FiCpu />,
    },
    {
      title: "Task Orchestration",
      description:
        "AI supervisors distribute tasks across specialized sub-agents, track progress, and consolidate outcomes.",
      icon: <FiGitBranch />,
    },
    {
      title: "Autonomous Tool Use",
      description:
        "Secure tool gateways let agents access CRMs, code repos, RPA bots, and data warehouses with audit trails.",
      icon: <MdAutoMode />,
    },
  ];

  const steps = [
    {
      title: "Opportunity Framing",
      description:
        "Identify processes that benefit from autonomous reasoning and define KPIs, guardrails, and human oversight.",
    },
    {
      title: "Agent Architecture Design",
      description:
        "Select model stack, memory strategy, and tool interfaces required for multi-agent collaboration.",
    },
    {
      title: "Build & Simulation",
      description:
        "Implement agents, run simulations, and stress-test scenarios before live deployment.",
    },
    {
      title: "Production Governance",
      description:
        "Deploy monitoring, evaluation, and fallback strategies to ensure safe, predictable outcomes.",
    },
  ];

  const benefits = [
    "Automate complex workflows that exceed traditional RPA",
    "Deliver consistent decisions using policy-aware reasoning",
    "Shorten time-to-value for data-heavy tasks with autonomous assistance",
    "Enable 24/7 operations without hand-offs or context loss",
    "Integrate with existing systems via secure tool execution",
    "Continuously improve through telemetry and human feedback",
  ];

  const expertise = [
    "Applied AI research team with backgrounds in agent frameworks and RLHF",
    "Experience with LangChain, AutoGen, CrewAI, and custom orchestration layers",
    "Memory engineering using Pinecone, Weaviate, Redis, and pgvector",
    "Robust evaluation harnesses for hallucination, bias, and safety checks",
    "Compliance-first design with audit logs and role-based permissions",
    "Partnerships with hyperscalers for scalable GPU and inference stacks",
  ];

   const image = [
    {
      side_image: "../assets/images/ai-automation/4.png",
    }

  ];

  const useCases = [
    {
      industry: "Capital Markets",
      title: "Autonomous Research Desk",
      description:
        "Agents synthesize filings, news, and market data to draft investment briefs and alerts.",
    },
    {
      industry: "Logistics",
      title: "Network Optimization Agent",
      description:
        "Multi-agent system plans routes, negotiates carrier slots, and coordinates exception handling.",
    },
    {
      industry: "Software",
      title: "DevOps Co-Pilot",
      description:
        "Agents triage incidents, propose fixes, and trigger CI/CD actions with human approval.",
    },
    {
      industry: "Enterprise IT",
      title: "Autonomous Service Desk",
      description:
        "Self-learning agents resolve device issues, manage access, and orchestrate backend automations.",
    },
  ];

  const reasons = [
    "Proven agent blueprints tailored to industry-grade guardrails",
    "Hybrid team of architects, ML engineers, and domain SMEs",
    "Sandbox environments for rapid experimentation and validation",
    "Secure integration accelerators for ERP, CRM, and data stacks",
    "Co-innovation model with shared KPIs and governance councils",
    "Continuous improvement cycles via telemetry-driven insights",
  ];

  return (
    <AIAutomationTemplate
      title="Autonomous AI Agent"
      heroDescription="Launch autonomous agents capable of reasoning, planning, and executing multi-step workflows across your enterprise."
      about={{
        heading: "About Autonomous AI Agent Services",
        description:
          "We design agent architectures that blend advanced reasoning, tool use, and governance so your teams delegate high-value work with confidence.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Design an Agent Strategy"
      meta={{
        title: "Autonomous AI Agent | Agentic Automation | Ayanshtech",
        description:
          "Build multi-step reasoning agents with self-learning behavior, long-context decisioning, and task orchestration.",
        ogTitle: "Autonomous AI Agent Solutions",
        ogDescription:
          "Unlock agentic workflows with autonomous AI capable of planning, tool use, and continuous learning.",
        ogUrl: "http://ayanshtechnology.com/autonomous-ai-agent",
        canonical: "http://ayanshtechnology.com/autonomous-ai-agent",
      }}
    />
  );
};

export default AutonomousAIAgent;


